/**
 * batch-70 wagenpark-week (FO-528 §A2) - parser voor de wekelijkse import van het
 * telematica-exportbestand (xlsx of csv): per voertuig km-standen en ritten.
 *
 * Werkt op de CSV-weergave van elk werkblad (een xlsx wordt in de parser-worker eerst
 * naar CSV omgezet, zoals bij het km-rapport). Kolommen worden op hun kop gevonden,
 * niet op positie, en elke regel is óf een stand (kenteken + datum + km-stand), óf
 * een rit (kenteken + start + eind + km), of allebei (een rit met de stand bij
 * aankomst). Ongeldige regels komen apart terug met een reden: nooit stil gedropt.
 *
 * Privacy (FO-528 §A1): van een rit bewaren we alleen de postcodecijfers (PC4) en de plaats
 * van start en eind, nooit een adres, de letters van de postcode of een GPS-coördinaat;
 * kolommen met coördinaten worden niet gelezen.
 */

import type { RitClassificatie } from '@streep/types'
import { amsterdamWallclockToUtc } from './amsterdam-tijd'
import { normalizeKenteken } from './voertuig-utils'
import { postcodeNiveau } from './wagenpark-week'

export interface ImportStand {
  regel: number
  kenteken: string
  op: Date
  kmStand: number
  /** De datum had geen tijd: de stand is die van het einde van de dag (23:59 NL). */
  dagstand: boolean
}

export interface ImportRit {
  regel: number
  kenteken: string
  externeRitId: string | null
  start: Date
  eind: Date
  km: number
  startPostcode: string | null
  eindPostcode: string | null
  startPlaats: string | null
  eindPlaats: string | null
  classificatie: RitClassificatie
}

export interface ImportOngeldig {
  regel: number
  reden: string
}

export interface TelematicaImportParse {
  kolomkopGevonden: boolean
  standen: ImportStand[]
  ritten: ImportRit[]
  ongeldig: ImportOngeldig[]
}

/** Splits één CSV-regel (komma of puntkomma; dubbele aanhalingstekens). */
export function splitsImportCsvRegel(regel: string, scheiding: ',' | ';'): string[] {
  const cellen: string[] = []
  let huidig = ''
  let inQuote = false
  for (let i = 0; i < regel.length; i++) {
    const c = regel[i]!
    if (inQuote) {
      if (c === '"' && regel[i + 1] === '"') {
        huidig += '"'
        i++
      } else if (c === '"') inQuote = false
      else huidig += c
    } else if (c === '"') inQuote = true
    else if (c === scheiding) {
      cellen.push(huidig.trim())
      huidig = ''
    } else huidig += c
  }
  cellen.push(huidig.trim())
  return cellen
}

function kiesScheiding(regel: string): ',' | ';' {
  return (regel.match(/;/g)?.length ?? 0) > (regel.match(/,/g)?.length ?? 0) ? ';' : ','
}

/**
 * batch-70 wagenpark-week (A5, privacy-arm) - alleen een plaatsnaam, nooit een adres. Uit
 * "Kanaalweg 12, 3511 AB Utrecht" blijft "Utrecht"; een tekst met een huisnummer en zonder
 * plaats ("Kanaalweg 12") wordt niets. Hooguit 40 tekens.
 */
const LANDEN = new Set(['nederland', 'netherlands', 'the netherlands', 'nl', 'belgie', 'belgië', 'belgium', 'duitsland', 'deutschland', 'germany'])

export function alleenPlaatsnaam(tekst: string | null | undefined): string | null {
  const t = (tekst ?? '').trim()
  if (!t) return null
  // Een land achteraan ("..., Nederland") is geen plaats (review L-6).
  const delen = t
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean)
  while (delen.length > 1 && LANDEN.has(delen[delen.length - 1]!.toLowerCase())) delen.pop()
  const kandidaat = (delen.length > 1 ? delen[delen.length - 1]! : (delen[0] ?? t))
    // Een postcode (1234 AB) of losse cijfers eraf.
    .replace(/\b\d{4}\s?[A-Za-z]{2}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!kandidaat || /\d/.test(kandidaat)) return null
  return kandidaat.slice(0, 40)
}

const KOPPEN = {
  kenteken: ['kenteken', 'license plate', 'registration', 'voertuig'],
  datum: ['datum', 'date', 'peildatum'],
  tijd: ['tijd', 'time'],
  kmStand: ['km-stand', 'kmstand', 'kilometerstand', 'kilometerteller', 'odometer', 'km stand', 'tellerstand', 'km-stand eind', 'kilometerteller (aankomst) km'],
  ritId: ['rit-id', 'ritid', 'ritnummer', 'trip id', 'tripid', 'rit'],
  start: ['start', 'vertrek', 'begin', 'starttijd', 'vertrektijd', 'start time'],
  eind: ['eind', 'aankomst', 'einde', 'eindtijd', 'aankomsttijd', 'end time'],
  km: ['km', 'afstand', 'afstand (km)', 'distance', 'gereden km', 'ritafstand'],
  startPostcode: ['postcode start', 'startpostcode', 'postcode vertrek', 'van postcode', 'start postcode'],
  eindPostcode: ['postcode eind', 'eindpostcode', 'postcode aankomst', 'naar postcode', 'eind postcode'],
  // Geen kale "van"/"naar": daar staat meestal een adres (privacy, A5: alleen de plaats).
  startPlaats: ['plaats start', 'startplaats', 'plaats vertrek', 'van plaats', 'vertrekplaats'],
  eindPlaats: ['plaats eind', 'eindplaats', 'plaats aankomst', 'naar plaats', 'aankomstplaats'],
  classificatie: ['type', 'rittype', 'privé/zakelijk', 'prive/zakelijk', 'classificatie', 'soort rit', 'trip type', 'ritsoort'],
} as const

type Kolom = keyof typeof KOPPEN

function zoekKop(cellen: string[]): Partial<Record<Kolom, number>> | null {
  const kop = cellen.map((c) => c.trim().toLowerCase())
  const uit: Partial<Record<Kolom, number>> = {}
  for (const [veld, namen] of Object.entries(KOPPEN) as Array<[Kolom, readonly string[]]>) {
    const i = kop.findIndex((c) => namen.includes(c))
    if (i >= 0) uit[veld] = i
  }
  if (uit.kenteken === undefined) return null
  const standMogelijk = uit.datum !== undefined && uit.kmStand !== undefined
  const ritMogelijk = uit.start !== undefined && uit.eind !== undefined && uit.km !== undefined
  return standMogelijk || ritMogelijk ? uit : null
}

/** Km-getal ("48.210", "48210,5", "48,210.5"); afgerond op hele km voor standen. */
export function parseImportGetal(cel: string): number | null {
  const ruw = cel.replace(/\s/g, '').replace(/km$/i, '')
  if (!/^\d+(?:[.,]\d+)*$/.test(ruw)) return null
  if (/^\d{1,3}(?:\.\d{3})+$/.test(ruw) || /^\d{1,3}(?:,\d{3})+$/.test(ruw)) return Number(ruw.replace(/[.,]/g, ''))
  const m = /^(.*)[.,](\d+)$/.exec(ruw)
  const geheel = (m ? (m[1] ?? '') : ruw).replace(/[.,]/g, '')
  const n = Number(m ? `${geheel}.${m[2] ?? '0'}` : geheel)
  return Number.isFinite(n) ? n : null
}

/**
 * Datum (en optioneel tijd) in NL-wandklok naar UTC: "01-11-2026", "1-11-2026 23:59",
 * "2026-11-01", "2026-11-01T07:40", "2026-11-01 07:40:12". Zonder tijd: null-tijd.
 */
export function parseImportMoment(datum: string, tijd?: string | null): { op: Date; metTijd: boolean } | null {
  const d = datum.trim()
  let ymd: string | null = null
  let hm: string | null = null
  const nl = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/.exec(d)
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?(?:Z)?$/.exec(d)
  if (nl) {
    ymd = `${nl[3]}-${nl[2]!.padStart(2, '0')}-${nl[1]!.padStart(2, '0')}`
    if (nl[4]) hm = `${nl[4].padStart(2, '0')}:${nl[5]}`
  } else if (iso) {
    ymd = `${iso[1]}-${iso[2]}-${iso[3]}`
    if (iso[4]) hm = `${iso[4].padStart(2, '0')}:${iso[5]}`
  }
  if (!ymd) return null
  if (!hm && tijd) {
    const t = /^(\d{1,2}):(\d{2})/.exec(tijd.trim())
    if (t) hm = `${t[1]!.padStart(2, '0')}:${t[2]}`
  }
  const maand = Number(ymd.slice(5, 7))
  const dag = Number(ymd.slice(8, 10))
  if (maand < 1 || maand > 12 || dag < 1 || dag > 31) return null
  try {
    return { op: amsterdamWallclockToUtc(ymd, hm ?? '23:59'), metTijd: hm != null }
  } catch {
    return null
  }
}

export function parseRitClassificatie(cel: string | null | undefined): RitClassificatie {
  const c = (cel ?? '').trim().toLowerCase()
  if (!c) return 'ONBEKEND'
  if (c.startsWith('priv') || c === 'p' || c === 'private') return 'PRIVE'
  if (c.includes('woon') || c === 'commute' || c === 'ww') return 'WOONWERK'
  if (c.startsWith('zak') || c === 'z' || c === 'business') return 'ZAKELIJK'
  return 'ONBEKEND'
}

const MAX_KM_STAND = 2_000_000
const MAX_RIT_KM = 2_000

/**
 * Parse de CSV-weergave van alle werkbladen. Per werkblad de eerste regel met een
 * herkenbare kop (kenteken plus datum/km-stand of start/eind/km); de regels daarna
 * worden gelezen tot het eind van het blad.
 */
export function parseTelematicaImport(sheets: Record<string, string>): TelematicaImportParse {
  const standen: ImportStand[] = []
  const ritten: ImportRit[] = []
  const ongeldig: ImportOngeldig[] = []
  let kolomkopGevonden = false
  let regelNr = 0
  for (const csv of Object.values(sheets)) {
    let kop: Partial<Record<Kolom, number>> | null = null
    let scheiding: ',' | ';' = ','
    for (const regel of csv.split(/\r?\n/)) {
      regelNr += 1
      if (!kop) {
        scheiding = kiesScheiding(regel)
        kop = zoekKop(splitsImportCsvRegel(regel, scheiding))
        if (kop) kolomkopGevonden = true
        continue
      }
      const cellen = splitsImportCsvRegel(regel, scheiding)
      if (cellen.every((c) => c === '')) continue
      const cel = (k: Kolom): string => (kop![k] !== undefined ? (cellen[kop![k]!] ?? '') : '')
      const kenteken = normalizeKenteken(cel('kenteken'))
      if (!kenteken) {
        ongeldig.push({ regel: regelNr, reden: 'Geen kenteken' })
        continue
      }
      let gelezen = false
      // Rit: start, eind en km.
      if (cel('start') && cel('eind') && cel('km')) {
        const start = parseImportMoment(cel('start'))
        const eind = parseImportMoment(cel('eind'))
        const km = parseImportGetal(cel('km'))
        if (!start || !eind || !start.metTijd || !eind.metTijd) ongeldig.push({ regel: regelNr, reden: 'Start of eind van de rit is geen datum met tijd' })
        else if (eind.op.getTime() < start.op.getTime()) ongeldig.push({ regel: regelNr, reden: 'Eind van de rit ligt voor de start' })
        else if (km == null || km < 0 || km > MAX_RIT_KM) ongeldig.push({ regel: regelNr, reden: `Ritafstand ontbreekt of is onmogelijk (${cel('km') || 'leeg'})` })
        else {
          ritten.push({
            regel: regelNr,
            kenteken,
            externeRitId: cel('ritId') || null,
            start: start.op,
            eind: eind.op,
            km: Math.round(km * 10) / 10,
            startPostcode: postcodeNiveau(cel('startPostcode')),
            eindPostcode: postcodeNiveau(cel('eindPostcode')),
            startPlaats: alleenPlaatsnaam(cel('startPlaats')),
            eindPlaats: alleenPlaatsnaam(cel('eindPlaats')),
            classificatie: parseRitClassificatie(cel('classificatie')),
          })
          gelezen = true
        }
      }
      // Stand: km-stand met een datum (of de eindtijd van de rit).
      if (cel('kmStand')) {
        const km = parseImportGetal(cel('kmStand'))
        const moment = cel('datum') ? parseImportMoment(cel('datum'), cel('tijd') || null) : cel('eind') ? parseImportMoment(cel('eind')) : null
        if (km == null || km <= 0 || km > MAX_KM_STAND) ongeldig.push({ regel: regelNr, reden: `Km-stand ontbreekt of is onmogelijk (${cel('kmStand')})` })
        else if (!moment) ongeldig.push({ regel: regelNr, reden: 'Geen geldige datum bij de km-stand' })
        else {
          standen.push({ regel: regelNr, kenteken, op: moment.op, kmStand: Math.round(km), dagstand: !moment.metTijd })
          gelezen = true
        }
      }
      if (!gelezen && !ongeldig.some((o) => o.regel === regelNr)) {
        ongeldig.push({ regel: regelNr, reden: 'Regel bevat geen km-stand en geen rit' })
      }
    }
  }
  return { kolomkopGevonden, standen, ritten, ongeldig }
}
